import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Flex,
    Link,
    Tooltip,
    Avatar,
    IconButton,
    useToast,
    Image,
    Text,
    Spinner,
    Button,
    Divider,
    HStack,
    Heading,
    useColorModeValue,
    usePrefersReducedMotion,
    Badge,
    Icon,
} from "@chakra-ui/react";
import { ref, remove, update } from "firebase/database";
import { db } from "../firebase";
import { FaThumbsUp, FaThumbsDown, FaVolumeUp, FaCopy, FaPause, FaTrophy, FaEquals, FaEye, FaEyeSlash, FaCheck, FaTimes, FaFileAlt, FaChevronRight } from 'react-icons/fa';
import { AlternativeMessagesInterface, MessageInterface } from "../configs";
import fetchStreamAudio from "../helpers/fetchStreamAudio";
import { LegalDraftArtifact, createLegalDraftArtifact, splitLegalDraftContent } from "../helpers/legalArtifacts";
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { prepareText, getLabelFromUrl } from '../helpers/prepareMessageText';
import { keyframes } from "@emotion/react";
import { isLegalCodeUrl, isSullivanOnCompUrl } from "../helpers/urlSafety";
import CalcCards, { CalcFigure, computedFigures, markComputedFigures, type CalcResult } from "./CalcCards";

const pulseBorder = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(23, 64, 77, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(23, 64, 77, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(23, 64, 77, 0);
  }
`;

interface AssistantBoxProps {
    box: MessageInterface;
    idx: number;
    currentChat: string;
    allReferences: any[];
    showThumbs: boolean;
    apiAddress: string;
    status: string;
    alternativeMessages: AlternativeMessagesInterface;
    canLinkCaseCitations?: boolean;
    onOpenLegalDraft?: (artifact: LegalDraftArtifact) => void;
    showSources?: boolean;
    onSourcesVisibilityChange?: (showSources: boolean) => void;
    /** The `calculate` results behind this answer, shown under it. */
    calcs?: CalcResult[];
}

type CaseCitationStatus = "verified" | "failed";

interface CaseCitationStatusBadgeProps {
    status: CaseCitationStatus;
}

const CaseCitationStatusBadge: React.FC<CaseCitationStatusBadgeProps> = ({ status }) => {
    const isVerified = status === "verified";
    const tooltipLabel = isVerified
        ? "This case is cited in Sullivan on Comp"
        : "This case is not cited in Sullivan on Comp";

    const badge = (
        <Badge
            as="span"
            colorScheme={isVerified ? "green" : "red"}
            variant="subtle"
            ml="5px"
            w="16px"
            h="16px"
            minW="16px"
            p={0}
            borderRadius="full"
            verticalAlign="middle"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            lineHeight="0"
            cursor="help"
            position="relative"
            role="img"
            aria-label={tooltipLabel}
        >
            <Icon
                as={isVerified ? FaCheck : FaTimes}
                boxSize={isVerified ? "8px" : "7px"}
                display="block"
                aria-hidden="true"
            />
        </Badge>
    );

    return (
        <Tooltip label={tooltipLabel} placement="top" openDelay={100} closeDelay={50} hasArrow closeOnScroll>
            {badge}
        </Tooltip>
    );
};

const AssistantBox: React.FC<AssistantBoxProps> = ({ box, idx, currentChat, allReferences = [], showThumbs, apiAddress, status, alternativeMessages, canLinkCaseCitations = false, onOpenLegalDraft, showSources: controlledShowSources, onSourcesVisibilityChange, calcs = [] }) => {
    const [showAlternativeAnswer, setShowAlternativeAnswer] = useState<boolean>(false);
    const [audioPlaying, setAudioPlaying] = useState<boolean>(false);
    const [audioLoading, setAudioLoading] = useState<boolean>(false);
    const [localShowSources, setLocalShowSources] = useState<boolean>(true);
    const [currentAlternative, setCurrentAlternative] = useState<any>(null);
    // A box that mounts with text already there (an answer remounted when the final messages replace the streamed
    // ones, or one loaded from history) shows it at once: starting empty would collapse it for a frame and move the
    // reader's view. A new answer still types in, because its box mounts while its text is empty.
    const [displayedText, setDisplayedText] = useState<string>(() => box.content?.[0]?.text || '');
    const displayedTextRef = useRef<string>(box.content?.[0]?.text || '');
    const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const toast = useToast();
    const prefersReducedMotion = usePrefersReducedMotion();
    const targetText = box.content?.[0]?.text || '';
    const legalDraftArtifact = React.useMemo(
        () => createLegalDraftArtifact(targetText, idx),
        [targetText, idx]
    );
    const artifactKindLabel = legalDraftArtifact?.kind === 'memo'
            ? 'Memo'
            : legalDraftArtifact?.kind === 'letter'
                ? 'Letter'
                : 'Draft';
    const artifactActionLabel = status === 'completed'
        ? 'Open workspace to fill, edit, and export'
        : 'Workspace will open when drafting finishes';
    const canOpenLegalDraft = status === 'completed' && !!onOpenLegalDraft;
    const showSources = controlledShowSources ?? localShowSources;
    const setSourcesVisible = React.useCallback((nextShowSources: boolean) => {
        if (controlledShowSources === undefined) {
            setLocalShowSources(nextShowSources);
        }
        onSourcesVisibilityChange?.(nextShowSources);
    }, [controlledShowSources, onSourcesVisibilityChange]);
    const updateDisplayedText = React.useCallback((value: string) => {
        displayedTextRef.current = value;
        setDisplayedText(value);
    }, []);

    // Theme Variables
    const boxBg = useColorModeValue("#ffffff", "rgba(26, 32, 44, 0.78)");
    const boxBorderColor = useColorModeValue("rgba(23, 64, 78, 0.13)", "whiteAlpha.200");
    const textColor = useColorModeValue("gray.700", "gray.200");
    const labelColor = useColorModeValue("#17404e", "#4FD1C5");
    const linkColor = useColorModeValue("#0284c7", "blue.300");
    const linkHoverColor = useColorModeValue("#0369a1", "blue.200");
    const headingColor = useColorModeValue("gray.900", "white");
    const subHeadingColor = useColorModeValue("gray.800", "gray.300");
    const blockquoteBg = useColorModeValue("blue.50", "blue.900");
    const codeBg = useColorModeValue("gray.100", "gray.700");
    const codeColor = useColorModeValue("pink.600", "pink.300");
    const codeBorderColor = useColorModeValue("gray.200", "gray.600");
    const tableBorderColor = useColorModeValue("gray.200", "gray.600");
    const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
    const tableStripeBg = useColorModeValue("gray.50", "whiteAlpha.50");
    const hrColor = useColorModeValue("gray.300", "gray.600");
    const iconColor = useColorModeValue("gray.600", "gray.400");
    const iconHoverBg = useColorModeValue("gray.50", "gray.600");
    const thumbsUpBg = useColorModeValue("#e6f7ed", "rgba(34, 197, 94, 0.2)");
    const thumbsUpColor = useColorModeValue("#22c55e", "#4ade80");
    const thumbsDownBg = useColorModeValue("#fee2e2", "rgba(239, 68, 68, 0.2)");
    const thumbsDownColor = useColorModeValue("#ef4444", "#f87171");
    const audioPlayingBg = useColorModeValue("#e0f2fe", "rgba(2, 132, 199, 0.2)");
    const audioPlayingColor = useColorModeValue("#0284c7", "#38bdf8");
    const spinnerColor = useColorModeValue("#17404d", "#4FD1C5");
    const timeColor = useColorModeValue("gray.400", "gray.500");
    const panelShadow = useColorModeValue("0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 24px rgba(15, 23, 42, 0.035)", "none");
    const boxHoverBorderColor = useColorModeValue("rgba(23, 64, 78, 0.22)", "whiteAlpha.300");
    const artifactCardBg = useColorModeValue("rgba(255, 255, 255, 0.82)", "whiteAlpha.100");
    const artifactCardBorder = useColorModeValue("rgba(23, 64, 78, 0.14)", "whiteAlpha.300");
    const artifactCardHover = useColorModeValue("rgba(247, 251, 252, 0.95)", "whiteAlpha.200");
    const artifactIconBg = useColorModeValue("rgba(23, 64, 78, 0.07)", "whiteAlpha.100");
    const artifactMetaBg = useColorModeValue("rgba(23, 64, 78, 0.06)", "whiteAlpha.100");
    const artifactMetaColor = useColorModeValue("rgba(23, 64, 78, 0.72)", "gray.300");
    const artifactCardShadow = useColorModeValue("0 1px 2px rgba(15, 23, 42, 0.04)", "none");


    // Typing animation effect
    useEffect(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        // If message is complete or motion should be reduced, show full text immediately.
        if (status === 'completed' || prefersReducedMotion) {
            updateDisplayedText(targetText);
            return;
        }

        // If target text is shorter (shouldn't happen but just in case), update immediately
        if (targetText.length < displayedTextRef.current.length) {
            updateDisplayedText(targetText);
            return;
        }

        // If we're already showing the full text, no need to animate
        if (displayedTextRef.current === targetText) {
            return;
        }

        let currentIndex = displayedTextRef.current.length;

        const animate = () => {
            if (currentIndex < targetText.length) {
                const remainingCharacters = targetText.length - currentIndex;
                const charactersPerFrame = Math.min(
                    90,
                    Math.max(10, Math.ceil(remainingCharacters / 8))
                );
                currentIndex = Math.min(currentIndex + charactersPerFrame, targetText.length);
                updateDisplayedText(targetText.slice(0, currentIndex));
                animationFrameRef.current = requestAnimationFrame(animate);
            }
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [targetText, status, prefersReducedMotion, updateDisplayedText]);

    // Update currentAlternative whenever alternativeMessages changes
    useEffect(() => {
        if (alternativeMessages?.answers) {
            const alternatives = Object.values(alternativeMessages.answers);
            const currentAlt = alternatives.find(x => x?.index === idx);
            setCurrentAlternative(currentAlt || null);
        } else {
            setCurrentAlternative(null);
        }
    }, [alternativeMessages, idx]);

    function thumbsUp(box: MessageInterface, idx: number) {
        if (box.review && box.review.thumbs === 'up') {
            const thumbsDownRef = ref(db, 'rawChatMessages/' + currentChat + '/' + idx + '/review');
            remove(thumbsDownRef);
        } else {
            const thumbsDownRef = ref(db, 'rawChatMessages/' + currentChat + '/' + idx);
            update(thumbsDownRef, { review: { thumbs: 'up' } });
        }
    }

    function thumbsDown(box: MessageInterface, idx: number) {
        if (box.review && box.review.thumbs === 'down') {
            const thumbsDownRef = ref(db, 'rawChatMessages/' + currentChat + '/' + idx + '/review');
            remove(thumbsDownRef);
        } else {
            const thumbsDownRef = ref(db, 'rawChatMessages/' + currentChat + '/' + idx);
            update(thumbsDownRef, { review: { thumbs: 'down' } });
        }
    }

    function prepareMessage(text: string, showSources: boolean, renderCaseCitationControls = true) {
        return prepareText(text, showSources, allReferences, {
            renderCaseCitationControls,
            canLinkCaseCitations,
        });
    }

    function copyMessage(message: string) {
        // Always copy full text with citations, without UI-only badges/links
        message = prepareMessage(message, true, false);
        navigator.clipboard.writeText(message);
        toast({
            position: 'top',
            title: 'Message copied to clipboard',
            status: 'success',
            duration: 5000,
            isClosable: true,
        });
    }

    function handleShowAlternativeAnswer() {
        if (!currentAlternative.seen) {
            const key = Object.keys(alternativeMessages.answers).find(
                (x) => alternativeMessages.answers[x].index === idx
            );
            if (key) {
                update(ref(db, 'alternativeMessages/' + currentChat + '/answers/' + key), {
                    seen: true
                });
            }
        }
        setShowAlternativeAnswer(!showAlternativeAnswer);
    }

    function handlePickFavoriteAnswer(votingResult: string) {
        const key = Object.keys(alternativeMessages.answers).find(
            (x) => alternativeMessages.answers[x].index === idx
        );
        if (key) {
            update(ref(db, 'alternativeMessages/' + currentChat + '/answers/' + key), {
                result: votingResult,
            });
            if (votingResult === "better") {
                update(ref(db, 'rawChatMessages/' + currentChat), {
                    [idx]: currentAlternative.message
                });
                update(ref(db, 'chats/' + currentChat), {
                    references: alternativeMessages.references ? alternativeMessages.references : []
                });
            }
        }
        setShowAlternativeAnswer(false);
    }

    function displayText(box: any, isAlternative: boolean = false) {
        // Use animated text for the main message, actual text for alternatives
        const rawText = isAlternative ? (box.content?.[0]?.text || '') : displayedText;
        const draftParts = splitLegalDraftContent(rawText);
        // Dollar figures a calculation produced are marked in the answer (not in an alternative answer).
        const figures = computedFigures(isAlternative ? [] : calcs);
        const text = markComputedFigures(prepareMessage(draftParts.before, showSources), figures);
        const afterArtifactText = markComputedFigures(prepareMessage(draftParts.after, showSources), figures);

        return (
            <Box
                fontSize="15.5px"
                lineHeight="1.8"
                fontFamily="Bitter, serif"
                w="100%"
                fontWeight={400}
                px={{ base: 4, md: 6 }}
                py={{ base: 4, md: 5 }}
                boxShadow={panelShadow}
                borderRadius="8px"
                bg={boxBg}
                border="1px solid"
                borderColor={boxBorderColor}
                position="relative"
                overflow="hidden"
                transition="border-color 0.15s ease, box-shadow 0.15s ease"
                _hover={{
                    borderColor: boxHoverBorderColor,
                    boxShadow: panelShadow,
                }}
            >
                <Markdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                        span: ({ node, ...props }) => {
                            const p: any = props;
                            if (p['data-verified'] === "1") {
                                return <CaseCitationStatusBadge status="verified" />;
                            }
                            if (p['data-failed'] === "1") {
                                return <CaseCitationStatusBadge status="failed" />;
                            }
                            if (p['data-calc-fig'] === "1") {
                                return <CalcFigure note={p['data-calc-note'] || ''}>{p.children}</CalcFigure>;
                            }
                            return <Box as="span" {...props} />;
                        },
                        a: ({ node, href, ...props }) => {
                            // Prefer Markdown's title (e.g., [text](url "hover")) for tooltip.
                            // Fall back to derived label from URL when available.
                            const anyProps: any = props;
                            const isCaseCitationLink = anyProps?.['data-case-citation-link'] === '1';
                            const markdownTitle: string | undefined = anyProps?.title;
                            const tooltipLabel = markdownTitle ?? (isCaseCitationLink ? 'Open precedent case' : getLabelFromUrl(href, allReferences));

                            // Remove native title to avoid slow browser tooltips and double-tooltips.
                            const { title: _omitTitle, ...rest } = anyProps || {};

                            // If we desire to hide sources, and this looks like a SOC link, hide it.
                            // This acts as a fallback for any citations that escaped the regex.
                            const isSOC = !isLegalCodeUrl(href)
                                && (
                                    (typeof anyProps?.children === 'string' && anyProps.children.startsWith("SOC Section"))
                                    || isSullivanOnCompUrl(href)
                                );

                            if (!showSources && isSOC) {
                                return null;
                            }

                            const linkEl = (
                                <Link
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    href={href}
                                    isExternal
                                    aria-label={isCaseCitationLink ? 'Open precedent case' : undefined}
                                    color={linkColor}
                                    fontWeight={500}
                                    textDecoration="underline"
                                    textUnderlineOffset="2px"
                                    _hover={{
                                        color: linkHoverColor,
                                        textDecoration: "underline"
                                    }}
                                    {...rest}
                                />
                            );

                            // If we have a label, show a Chakra tooltip with minimal delay.
                            return tooltipLabel ? (
                                <Tooltip label={tooltipLabel} openDelay={100} closeDelay={50} hasArrow>
                                    {linkEl}
                                </Tooltip>
                            ) : (
                                linkEl
                            );
                        },
                        h1: ({ node, ...props }) => <Box as="h1" fontSize="24px" fontWeight="700" marginBottom="0.75em" marginTop="1em" color={headingColor} {...props} />,
                        h2: ({ node, ...props }) => <Box as="h2" fontSize="20px" fontWeight="700" marginBottom="0.75em" marginTop="1em" color={headingColor} {...props} />,
                        h3: ({ node, ...props }) => <Box as="h3" fontSize="18px" fontWeight="600" marginBottom="0.5em" marginTop="0.75em" color={subHeadingColor} {...props} />,
                        h4: ({ node, ...props }) => <Box as="h4" fontSize="16px" fontWeight="600" marginBottom="0.5em" marginTop="0.75em" color={subHeadingColor} {...props} />,
                        h5: ({ node, ...props }) => <Box as="h5" fontSize="14px" fontWeight="600" marginBottom="0.5em" marginTop="0.75em" color={subHeadingColor} {...props} />,
                        p: ({ node, ...props }) => <Box as="p" marginBottom="1em" color={textColor} lineHeight="1.8" {...props} />,
                        ul: ({ node, ...props }) => <Box as="ul" paddingLeft="24px" marginBottom="1em" listStyleType="disc" color={textColor} {...props} />,
                        ol: ({ node, ...props }) => <Box as="ol" paddingLeft="24px" marginBottom="1em" listStyleType="decimal" color={textColor} {...props} />,
                        li: ({ node, ...props }) => <Box as="li" marginBottom="0.5em" lineHeight="1.7" {...props} />,
                        hr: ({ node, ...props }) => <Box as="hr" marginY="1.5em" borderColor={hrColor} {...props} />,
                        table: ({ node, ...props }) => (
                            <Box
                                overflowX="auto"
                                marginY="1.25em"
                                border="1px solid"
                                borderColor={tableBorderColor}
                                borderRadius="8px"
                                maxW="100%"
                            >
                                <Box
                                    as="table"
                                    width="100%"
                                    minW="520px"
                                    fontFamily="Bitter, serif"
                                    fontSize="0.95em"
                                    lineHeight="1.55"
                                    color={textColor}
                                    sx={{ borderCollapse: "collapse" }}
                                    {...props}
                                />
                            </Box>
                        ),
                        thead: ({ node, ...props }) => (
                            <Box
                                as="thead"
                                bg={tableHeaderBg}
                                borderBottom="1px solid"
                                borderColor={tableBorderColor}
                                {...props}
                            />
                        ),
                        tbody: ({ node, ...props }) => (
                            <Box
                                as="tbody"
                                sx={{
                                    "tr:nth-of-type(even)": {
                                        background: tableStripeBg,
                                    },
                                }}
                                {...props}
                            />
                        ),
                        tr: ({ node, ...props }) => (
                            <Box
                                as="tr"
                                borderBottom="1px solid"
                                borderColor={tableBorderColor}
                                _last={{ borderBottom: 0 }}
                                {...props}
                            />
                        ),
                        th: ({ node, align, ...props }) => (
                            <Box
                                as="th"
                                px={3}
                                py={2.5}
                                textAlign={align === "center" || align === "right" ? align : "left"}
                                fontWeight={700}
                                color={headingColor}
                                verticalAlign="top"
                                borderRight="1px solid"
                                borderColor={tableBorderColor}
                                _last={{ borderRight: 0 }}
                                whiteSpace="normal"
                                wordBreak="break-word"
                                {...props}
                            />
                        ),
                        td: ({ node, align, ...props }) => (
                            <Box
                                as="td"
                                px={3}
                                py={2.5}
                                textAlign={align === "center" || align === "right" ? align : "left"}
                                verticalAlign="top"
                                borderRight="1px solid"
                                borderColor={tableBorderColor}
                                _last={{ borderRight: 0 }}
                                whiteSpace="normal"
                                wordBreak="break-word"
                                {...props}
                            />
                        ),
                        blockquote: ({ node, ...props }) => (
                            <Box
                                as="blockquote"
                                paddingLeft="1em"
                                paddingY="0.5em"
                                borderLeft="4px solid"
                                borderColor="blue.400"
                                marginY="1em"
                                fontStyle="italic"
                                bg={blockquoteBg}
                                borderRadius="4px"
                                color={textColor}
                                {...props}
                            />
                        ),
                        code: ({ node, ...props }) => (
                            <Box
                                as="code"
                                padding="0.2em 0.5em"
                                backgroundColor={codeBg}
                                borderRadius="6px"
                                fontFamily="'Monaco', 'Menlo', monospace"
                                fontSize="0.9em"
                                color={codeColor}
                                border="1px solid"
                                borderColor={codeBorderColor}
                                {...props}
                            />
                        ),
                    } as Components}
                >
                    {text}
                </Markdown>
                {legalDraftArtifact && (
                    <Box
                        as="button"
                        type="button"
                        mt={text ? 3 : 0}
                        mb={status === 'completed' ? 0 : 1}
                        w="100%"
                        textAlign="left"
                        border="1px solid"
                        borderColor={artifactCardBorder}
                        borderRadius="8px"
                        bg={artifactCardBg}
                        px={{ base: 3.5, md: 4 }}
                        py={3.5}
                        cursor={canOpenLegalDraft ? "pointer" : "default"}
                        boxShadow={artifactCardShadow}
                        transition="background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease"
                        _hover={canOpenLegalDraft ? {
                            bg: artifactCardHover,
                            borderColor: boxHoverBorderColor,
                            boxShadow: panelShadow,
                            transform: "translateY(-1px)",
                        } : undefined}
                        _active={canOpenLegalDraft ? { transform: "translateY(0)" } : undefined}
                        _focusVisible={{
                            outline: "none",
                            boxShadow: `0 0 0 2px ${boxBg}, 0 0 0 4px rgba(23, 64, 78, 0.22)`,
                        }}
                        onClick={() => {
                            if (canOpenLegalDraft) onOpenLegalDraft?.(legalDraftArtifact);
                        }}
                        aria-label={`Open ${legalDraftArtifact.title}`}
                        aria-disabled={!canOpenLegalDraft}
                    >
                        <Flex align="center" gap={3} minW={0}>
                            <Flex
                                align="center"
                                justify="center"
                                w="34px"
                                h="34px"
                                borderRadius="8px"
                                bg={artifactIconBg}
                                color={labelColor}
                                flexShrink={0}
                            >
                                <FaFileAlt size={14} aria-hidden />
                            </Flex>
                            <Box minW={0} flex={1}>
                                <Flex align="center" gap={2} mb={0.5} minW={0}>
                                    <Text
                                        fontFamily='"Elza", sans-serif'
                                        fontSize="13px"
                                        fontWeight={700}
                                        color={labelColor}
                                        noOfLines={1}
                                        minW={0}
                                    >
                                        {legalDraftArtifact.title}
                                    </Text>
                                    <Box
                                        as="span"
                                        px={2}
                                        py="2px"
                                        borderRadius="999px"
                                        bg={artifactMetaBg}
                                        color={artifactMetaColor}
                                        fontFamily='"Elza", sans-serif'
                                        fontSize="10px"
                                        fontWeight={700}
                                        lineHeight="1.25"
                                        textTransform="uppercase"
                                        flexShrink={0}
                                    >
                                        {artifactKindLabel}
                                    </Box>
                                </Flex>
                                <Text fontFamily='"Elza", sans-serif' fontSize="12px" color={iconColor} noOfLines={1}>
                                    {artifactActionLabel}
                                </Text>
                            </Box>
                            <Flex
                                align="center"
                                justify="center"
                                w="26px"
                                h="26px"
                                borderRadius="7px"
                                color={iconColor}
                                flexShrink={0}
                                opacity={canOpenLegalDraft ? 1 : 0.45}
                            >
                                <FaChevronRight size={11} aria-hidden />
                            </Flex>
                        </Flex>
                    </Box>
                )}
                {afterArtifactText && (
                    <Markdown
                        remarkPlugins={[remarkGfm, remarkBreaks]}
                        rehypePlugins={[rehypeRaw]}
                        components={{
                            span: ({ node, ...props }) => {
                                const p: any = props;
                                if (p['data-verified'] === "1") {
                                    return <CaseCitationStatusBadge status="verified" />;
                                }
                                if (p['data-failed'] === "1") {
                                    return <CaseCitationStatusBadge status="failed" />;
                                }
                                if (p['data-calc-fig'] === "1") {
                                    return <CalcFigure note={p['data-calc-note'] || ''}>{p.children}</CalcFigure>;
                                }
                                return <Box as="span" {...props} />;
                            },
                            a: ({ node, href, ...props }) => {
                                const anyProps: any = props;
                                const isCaseCitationLink = anyProps?.['data-case-citation-link'] === '1';
                                const markdownTitle: string | undefined = anyProps?.title;
                                const tooltipLabel = markdownTitle ?? (isCaseCitationLink ? 'Open precedent case' : getLabelFromUrl(href, allReferences));
                                const { title: _omitTitle, ...rest } = anyProps || {};
                                const isSOC = !isLegalCodeUrl(href)
                                    && (
                                        (typeof anyProps?.children === 'string' && anyProps.children.startsWith("SOC Section"))
                                        || isSullivanOnCompUrl(href)
                                    );

                                if (!showSources && isSOC) {
                                    return null;
                                }

                                const linkEl = (
                                    <Link
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                        href={href}
                                        isExternal
                                        aria-label={isCaseCitationLink ? 'Open precedent case' : undefined}
                                        color={linkColor}
                                        fontWeight={500}
                                        textDecoration="underline"
                                        textUnderlineOffset="2px"
                                        _hover={{
                                            color: linkHoverColor,
                                            textDecoration: "underline"
                                        }}
                                        {...rest}
                                    />
                                );

                                return tooltipLabel ? (
                                    <Tooltip label={tooltipLabel} openDelay={100} closeDelay={50} hasArrow>
                                        {linkEl}
                                    </Tooltip>
                                ) : (
                                    linkEl
                                );
                            },
                            h1: ({ node, ...props }) => <Box as="h1" fontSize="24px" fontWeight="700" marginBottom="0.75em" marginTop="1em" color={headingColor} {...props} />,
                            h2: ({ node, ...props }) => <Box as="h2" fontSize="20px" fontWeight="700" marginBottom="0.75em" marginTop="1em" color={headingColor} {...props} />,
                            h3: ({ node, ...props }) => <Box as="h3" fontSize="18px" fontWeight="600" marginBottom="0.5em" marginTop="0.75em" color={subHeadingColor} {...props} />,
                            p: ({ node, ...props }) => <Box as="p" marginBottom="1em" marginTop={legalDraftArtifact ? "1em" : undefined} color={textColor} lineHeight="1.8" {...props} />,
                            ul: ({ node, ...props }) => <Box as="ul" paddingLeft="24px" marginBottom="1em" listStyleType="disc" color={textColor} {...props} />,
                            ol: ({ node, ...props }) => <Box as="ol" paddingLeft="24px" marginBottom="1em" listStyleType="decimal" color={textColor} {...props} />,
                            li: ({ node, ...props }) => <Box as="li" marginBottom="0.5em" lineHeight="1.7" {...props} />,
                        } as Components}
                    >
                        {afterArtifactText}
                    </Markdown>
                )}
                {status === 'completed' && (
                    <Flex
                        mt={2}
                        pt={3}
                        gap={1}
                        alignItems="center"
                        justifyContent="flex-end"
                        flexWrap="wrap"
                        w="100%"
                        borderTop="1px solid"
                        borderColor={boxBorderColor}
                    >
                        {box.provider && (
                            <Tooltip label={box.provider === 'openai' ? "Answered using OpenAI" : "Answered using Claude"} placement="top" borderRadius="8px" closeOnScroll={true}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mr={1}
                                >
                                    <Image h="18px" w="18px" borderRadius="full" src={box.provider === 'openai' ? "openai_logo.png" : "claude_logo.png"} opacity={0.7} />
                                </Box>
                            </Tooltip>
                        )}
                        {currentAlternative && !currentAlternative.seen && (
                            <Button
                                borderRadius="full"
                                size="xs"
                                variant="outline"
                                borderColor="#17404D"
                                color="#17404D"
                                animation={`${pulseBorder} 2s infinite`}
                                onClick={() => { handleShowAlternativeAnswer(); }}
                                _hover={{
                                    bg: "#f0f9fc",
                                }}
                                transition="all 0.2s"
                                fontWeight={500}
                                fontSize="12px"
                            >
                                Alternative answer available
                            </Button>
                        )}
                        {showThumbs && !showAlternativeAnswer && (
                            <>
                                <Tooltip label="Like this answer" placement="top">
                                    <IconButton
                                        id={`ChatSOC - Response Liked ${idx}`}
                                        onClick={() => { thumbsUp(box, idx); }}
                                        size="xs"
                                        aria-label="Like"
                                        variant="ghost"
                                        bg={box.review && box.review.thumbs === 'up' ? thumbsUpBg : "transparent"}
                                        color={box.review && box.review.thumbs === 'up' ? thumbsUpColor : iconColor}
                                        borderRadius="md"
                                        _hover={{
                                            bg: box.review && box.review.thumbs === 'up' ? thumbsUpBg : iconHoverBg,
                                        }}
                                        transition="all 0.15s"
                                        icon={<FaThumbsUp style={{ pointerEvents: 'none' }} />}
                                    />
                                </Tooltip>
                                <Tooltip label="Dislike this answer" placement="top">
                                    <IconButton
                                        id={`ChatSOC - Response Disliked ${idx}`}
                                        onClick={() => { thumbsDown(box, idx) }}
                                        size="xs"
                                        aria-label="Dislike"
                                        variant="ghost"
                                        bg={box.review && box.review.thumbs === 'down' ? thumbsDownBg : "transparent"}
                                        color={box.review && box.review.thumbs === 'down' ? thumbsDownColor : iconColor}
                                        borderRadius="md"
                                        _hover={{
                                            bg: box.review && box.review.thumbs === 'down' ? thumbsDownBg : iconHoverBg,
                                        }}
                                        transition="all 0.15s"
                                        icon={<FaThumbsDown style={{ pointerEvents: 'none' }} />}
                                    />
                                </Tooltip>
                            </>
                        )}
                        <Tooltip label="Copy answer" placement="top">
                            <IconButton
                                size="xs"
                                onClick={() => {
                                    copyMessage(text);
                                }}
                                aria-label="Copy answer"
                                variant="ghost"
                                color={iconColor}
                                borderRadius="md"
                                _hover={{
                                    bg: iconHoverBg,
                                }}
                                transition="all 0.15s"
                                icon={<FaCopy />}
                            />
                        </Tooltip>
                        <Tooltip label={showSources ? "Hide SOC sources" : "Show SOC sources"} placement="top">
                            <IconButton
                                size="xs"
                                onClick={() => setSourcesVisible(!showSources)}
                                aria-label="Toggle sources"
                                variant="ghost"
                                color={iconColor}
                                borderRadius="md"
                                _hover={{
                                    bg: iconHoverBg,
                                }}
                                transition="all 0.15s"
                                icon={showSources ? <FaEyeSlash /> : <FaEye />}
                            />
                        </Tooltip>
                        <Tooltip label={
                            (box.content?.[0]?.text?.length || 0) > 4096
                                ? "Message too long for text-to-speech (max 4096 characters)"
                                : audioLoading
                                    ? "Generating audio..."
                                    : audioPlaying
                                        ? "Pause audio"
                                        : "Listen to answer"
                        } placement="top">
                            <IconButton
                                onClick={() => {
                                    streamAudio(box.content[0].text);
                                }}
                                isDisabled={(box.content?.[0]?.text?.length || 0) > 4096 || audioLoading}
                                size="xs"
                                aria-label="Listen answer"
                                variant="ghost"
                                bg={audioPlaying ? audioPlayingBg : "transparent"}
                                color={audioPlaying ? audioPlayingColor : iconColor}
                                borderRadius="md"
                                _hover={{
                                    bg: audioPlaying ? audioPlayingBg : iconHoverBg,
                                }}
                                transition="all 0.15s"
                                icon={audioLoading ? <Spinner size="xs" /> : (!audioPlaying ? <FaVolumeUp /> : <FaPause />)}
                            />
                        </Tooltip>
                    </Flex>
                )}
            </Box>
        );
    }

    async function streamAudio(text: string) {
        if (audioSourceRef.current) {
            audioSourceRef.current.stop();
            audioSourceRef.current = null;
            setAudioPlaying(false);
            return;
        }

        setAudioLoading(true);
        setAudioPlaying(true);
        try {
            await fetchStreamAudio(text, audioSourceRef, setAudioPlaying, apiAddress);
        } catch (error) {
            console.error('Error fetching the audio:', error);
            setAudioPlaying(false);
        } finally {
            setAudioLoading(false);
        }
    }

    function getStatusMessage(status: string) {
        if (status === 'running') {
            return "Evaluating question...";
        } else if (status === 'retrieving') {
            return "Looking for documents in Sullivan on Comp...";
        } else if (status === 'writing') {
            return "Working on the answer...";
        }
        return '';
    }

    function PreferenceSelector() {

        // A subtle gradient based on #163c49:
        const boxBg = "linear-gradient(135deg, #163c49 0%, #297492 100%)";

        // Lighten or darken text color for better contrast:
        const headingColor = "white";
        const infoTextColor = "whiteAlpha.800";

        // Use light borders on top of the dark background:
        const borderColor = "whiteAlpha.300";

        return (
            <Box
                maxW="lg"
                mx="auto"
                mt={8}
                p={8}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                bg={boxBg}
                boxShadow="md"
                textAlign="center"
                position="relative"
                overflow="hidden"
            >
                <Heading
                    mb={4}
                    color={headingColor}
                    fontSize="2xl"
                    letterSpacing="wide"
                >
                    Which answer do you prefer?
                </Heading>

                <Text mb={6} color={infoTextColor} fontSize="lg">
                    Please select your preference before continuing the conversation.
                </Text>

                <HStack spacing={5} justify="center">
                    <Tooltip label="Click if you prefer the first answer" placement="top">
                        <Button
                            leftIcon={<FaTrophy />}
                            onClick={() => handlePickFavoriteAnswer("worse")}
                            variant="outline"
                            size="lg"
                            borderRadius="30px"
                            borderColor="whiteAlpha.600"
                            color="whiteAlpha.900"
                            _hover={{
                                bg: "whiteAlpha.200",
                                transform: 'translateY(-2px)',
                                boxShadow: 'md',
                            }}
                            transition="all 0.2s"
                        >
                            Prefer #1
                        </Button>
                    </Tooltip>

                    <Tooltip label="Click if you feel they are equally good" placement="top">
                        <Button
                            leftIcon={<FaEquals />}
                            variant="outline"
                            size="lg"
                            onClick={() => handlePickFavoriteAnswer("equal")}
                            borderRadius="30px"
                            borderColor="whiteAlpha.600"
                            color="whiteAlpha.900"
                            _hover={{
                                bg: "whiteAlpha.200",
                                transform: 'translateY(-2px)',
                                boxShadow: 'md',
                            }}
                            transition="all 0.2s"
                        >
                            Equal
                        </Button>
                    </Tooltip>

                    <Tooltip label="Click if you prefer the second answer" placement="top">
                        <Button
                            leftIcon={<FaTrophy />}
                            onClick={() => handlePickFavoriteAnswer("better")}
                            variant="outline"
                            size="lg"
                            borderRadius="30px"
                            borderColor="whiteAlpha.600"
                            color="whiteAlpha.900"
                            _hover={{
                                bg: "whiteAlpha.200",
                                transform: 'translateY(-2px)',
                                boxShadow: 'md',
                            }}
                            transition="all 0.2s"
                        >
                            Prefer #2
                        </Button>
                    </Tooltip>
                </HStack>
            </Box>
        );
    }

    return (
        <Box
            mb={7}
            px={{ base: 4, md: 0 }}
        >
            <Flex align="center" gap={2} mb={1}>
                <Avatar h="22px" w="22px" src="chat_avatar.png" />
                <Text
                    fontSize="13px"
                    fontWeight={700}
                    color={labelColor}
                    letterSpacing="0.02em"
                >
                    ChatSOC
                </Text>
                {status !== 'completed' && (
                    <Flex align="center" gap={1.5}>
                        <Spinner size="xs" color={spinnerColor} speed="0.8s" />
                        <Text fontSize="12px" color={timeColor} fontWeight={500}>
                            {getStatusMessage(status)}
                        </Text>
                    </Flex>
                )}
                {status === 'completed' && box.time && (
                    <Text fontSize="11px" color={timeColor} fontWeight={400}>
                        {new Date(box.time).toLocaleString(undefined, {
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </Text>
                )}
            </Flex>
            {/* Text that already arrived stays visible while the run looks
                something up or is stalled. Hiding it made the answer look lost. */}
            {(status === 'completed' || targetText.length > 0) && (
                <Flex w="100%" gap={4} flexWrap="wrap">
                    {displayText(box)}
                    {currentAlternative?.seen && currentAlternative?.result === "pending" && (
                        <>
                            <Divider orientation="vertical" borderColor="gray.300" />
                            {displayText(currentAlternative.message, true)}
                        </>
                    )}
                </Flex>
            )}
            {status === 'completed' && <CalcCards calcs={calcs} />}
            {currentAlternative?.seen && currentAlternative?.result === "pending" && (
                PreferenceSelector()
            )}
        </Box>
    );
};

// Wrap the export with React.memo and provide our custom deep comparison function
export default React.memo(AssistantBox, (prevProps, nextProps) => {
    // 1. Basic scalar string/number comparisons
    if (prevProps.idx !== nextProps.idx) return false;
    if (prevProps.currentChat !== nextProps.currentChat) return false;
    if (prevProps.showThumbs !== nextProps.showThumbs) return false;
    if (prevProps.apiAddress !== nextProps.apiAddress) return false;
    if (prevProps.status !== nextProps.status) return false;
    if (prevProps.showSources !== nextProps.showSources) return false;
    if (prevProps.onSourcesVisibilityChange !== nextProps.onSourcesVisibilityChange) return false;
    if ((prevProps.calcs?.length ?? 0) !== (nextProps.calcs?.length ?? 0)) return false;

    // 2. Deep equality for the `box` message content
    // Check main text content
    const prevText = prevProps.box?.content?.[0]?.text;
    const nextText = nextProps.box?.content?.[0]?.text;
    if (prevText !== nextText) return false;

    // Check review status (thumbs up/down)
    if (prevProps.box?.review?.thumbs !== nextProps.box?.review?.thumbs) return false;

    // Check loading state
    if ((prevProps.box as any)?._loading !== (nextProps.box as any)?._loading) return false;

    // 3. Compare references length (this is usually sufficient, but we can do a deeper check if needed)
    if (prevProps.allReferences?.length !== nextProps.allReferences?.length) return false;

    // 4. Compare alternativeMessages (this is tricky because it's passed inline in ChatMessages right now)
    // If the answers or references objects changed structurally, we re-render.
    // For now we do a simple JSON stringify check since these aren't huge objects.
    if (JSON.stringify(prevProps.alternativeMessages) !== JSON.stringify(nextProps.alternativeMessages)) {
        return false;
    }

    return true;
});
